import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, categoryVideos, defaultCategorySlug, type VideoItem } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || defaultCategorySlug);
  const [videos, setVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    if (slug && categoryVideos[slug]) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setVideos(categoryVideos[activeCategory] || []);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const categoryTitle = currentCategory?.name || activeCategory;
  const categoryDesc = currentCategory?.description || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `红桃视频 - ${categoryTitle}分类`,
    description: `红桃视频${categoryTitle}分类，${categoryDesc}。浏览海量${categoryTitle}短视频，发现精彩内容。`,
    url: `https://hongtaovideo.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '红桃视频',
      url: 'https://hongtaovideo.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`红桃视频${categoryTitle}分类 - 海量${categoryTitle}短视频在线观看`}
        description={`红桃视频${categoryTitle}分类，${categoryDesc}。浏览海量${categoryTitle}短视频，发现精彩内容，尽在红桃视频。`}
        keywords={`红桃视频,${categoryTitle},${categoryTitle}视频,短视频,高清视频,红桃视频${categoryTitle}`}
        canonicalUrl={`https://hongtaovideo.com/category/${activeCategory}`}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类浏览', to: '/category/搞笑' },
                { label: categoryTitle },
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-600">{categoryDesc}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => {
                const isActive = activeCategory === cat.slug;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                );
              })}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {videos.map((video, index) => (
                <Link
                  key={index}
                  to={`/category/${activeCategory}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-background-200 mb-3">
                    <img
                      src={video.imageUrl}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-1.5 py-0.5 rounded">
                      {video.duration}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-1.5 group-hover:text-primary-500 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{video.author}</span>
                    <span>·</span>
                    <span>{video.views}次播放</span>
                  </div>
                </Link>
              ))}
            </div>

            {videos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}